import { Fragment, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMsal, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import * as jose from "jose";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFingerprint, faSignInAlt } from "@fortawesome/free-solid-svg-icons";
import Container from "../container";
import { useCookies } from "react-cookie";
import { aresPrivateKey, aresPublicKey } from "../../keys";
import { initSocket } from "../../transport";
import { loginRequest } from "../../authConfig";
import {
  userLoginRequest,
  userLogged,
  userLogOut,
  userSelectTenant,
  userSelectBranch
} from "../../redux/actions/user";

import "./style.scss";
import styles from "./styles.module.scss";
import pJson from "../../../package.json";
import axios from "axios";

const apiEndp = process.env.REACT_APP_API_ENDPOINT;

async function authenticateUser(user) {
  const alg = "RS256";
  const iss = "ares-control-center";
  const iat = Math.floor(Date.now() / 1000);
  const exp = iat + 36000;

  const publicKey = await jose.importSPKI(aresPublicKey, alg);
  const privateKey = await jose.importPKCS8(aresPrivateKey, alg);
  const loginToken = await new jose.SignJWT(user)
    .setProtectedHeader({ alg })
    .setIssuedAt(iat)
    .setIssuer(iss)
    .setExpirationTime(exp)
    .sign(privateKey);

  const { data } = await axios.get(`${apiEndp}/loginToken`, {
    headers: { loginToken }
  });

  const { payload } = await jose.jwtVerify(data, publicKey);

  return { ...payload, id_token: payload.token };
}

function Login() {
  const dispatch = useDispatch();
  const utente = useSelector((state) => state.user);
  const { instance, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount();

  const login = () => {
    instance
      .loginRedirect({
        ...loginRequest,
        prompt: "create"
      })
      .catch((error) => console.log(error));
  };

  const logout = () => {
    instance.logoutRedirect().catch((error) => console.log(error));
  };

  if (utente?.nickname) return <Tenant user={utente} logout={logout} />;

  if (activeAccount?.idTokenClaims) {
    const user = activeAccount.idTokenClaims;
    user.nickname = user.oid;

    authenticateUser(user).then((data) => {
      dispatch(userLogged(data));
    });
    return null;
  }

  return (
    <div className={styles.lgbx}>
      {/* <AuthenticatedTemplate>
        {activeAccount ? (
          <Container>
            <div>{activeAccount.idTokenClaims}</div>
          </Container>
        ) : null}
      </AuthenticatedTemplate> */}
      <UnauthenticatedTemplate>
        <div className={styles.loginContainer}>
          <div className={styles.titleBox}>
            <FontAwesomeIcon icon={faFingerprint} className={styles.logo} />
            <div className={styles.title} title="AR3S CONTROL CENTER">
              <span>AR3S</span>
              <span>CONTROL</span>
              <span>CENTER</span>
            </div>
          </div>
          <div className={styles.body}>
            Welcome to the AR3S CONTROL CENTER.
            <br />
            Please sign in to continue.
          </div>
          <div className={styles.footer}>
            <Button
              className="signInButton"
              onClick={login}
              disabled={inProgress !== InteractionStatus.None}
              variant="outline-dark"
              size="sm"
            >
              <FontAwesomeIcon icon={faSignInAlt} /> Sign In
            </Button>
          </div>
        </div>
      </UnauthenticatedTemplate>
    </div>
  );
}

function Tenant(props) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const dataKey = useMemo(
    () => `ares-login-data-${user.nickname}-${user.tenant}`,
    [user.nickname, user.tenant]
  );
  const [cookies, setCookie] = useCookies([dataKey]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState("");
  const [branch, setBranch] = useState(cookies[dataKey] ?? "test");
  const [error, setError] = useState("");

  const doLogout = useCallback(() => {
    setUsername("");
    setPassword("");
    setError("");
    dispatch(userLogOut());
    props.logout({ returnTo: window.location.origin });
  }, [dispatch, props]);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        userLoginRequest(
          username,
          password,
          () => setError(""),
          (error) => {
            setError("Som3thing w3nt wrong: T4k3 a br34th r3fr3sh your m3mory");
          }
        )
      );
    },
    [dispatch, password, username]
  );

  const onChooseTenant = useCallback(
    (e) => {
      e.preventDefault();

      if (tenant?.length && branch?.length) {
        initSocket(tenant, (socket) => {
          socket.emit("login", {
            tenant,
            branch,
            "id-token": user.id_token,
          });
          dispatch(userSelectTenant(tenant, branch, user));
        });
      }
    },
    [dispatch, tenant, branch, user]
  );

  const onChooseBranch = useCallback(
    (e) => {
      e.preventDefault();
      const branch = e.target.value;
      setBranch(branch);
      dispatch(userSelectBranch(branch));
      setCookie(dataKey, branch);
    },
    [dispatch, dataKey, setCookie]
  );

  // useEffect(() => {
  //   if (!user.active && userStored && Object.keys(userStored).length)
  //     dispatch(userLogged(userStored, false));
  // }, [dispatch, user, userStored]);

  const title = useMemo(() => {
    return (
      <div className={styles.titleBox}>
        <FontAwesomeIcon icon={faFingerprint} className={styles.logo} />
        <div className={styles.title} title="AR3S CONTROL CENTER">
          <span>AR3S</span>
          <span>CONTROL</span>
          <span>CENTER</span>
        </div>
      </div>
    );
  }, []);

  const footer = useMemo(() => {
    if (user.logging && !error) return null;

    if (!user.tenants) {
      return (
        <Button
          size="sm"
          type="submit"
          variant="outline-primary"
          onClick={onSubmit}
          key="login-btn"
        >
          Login
        </Button>
      );
    }

    const buttons = [
      <Button
        size="sm"
        variant="outline-dark"
        onClick={doLogout}
        className="logOutBtn"
        key="log-out-btn"
      >
        {error ? "Retry" : "Log Out"}
      </Button>
    ];

    if (user.tenants?.length > 0) {
      buttons.push(
        <Button
          size="sm"
          variant={!tenant || !branch ? "outline-secondary" : "outline-primary"}
          onClick={onChooseTenant}
          key="go-btn"
          disabled={!tenant || !branch}
        >
          Go
        </Button>
      );
    }

    return <>{buttons}</>;
  }, [
    doLogout,
    error,
    onChooseTenant,
    onSubmit,
    user.logging,
    user.tenants,
    tenant,
    branch
  ]);

  const extra = useMemo(() => {
    return <div className={styles.version}>{pJson.version}</div>;
  }, []);

  return (
    <div className={styles.animatedBackground}>
      {new Array(20).fill(1).map((_, i) => (
        <span key={`particle-${i}`} className={styles.particle} />
      ))}
      <Container
        title={title}
        className={styles.loginBox}
        color="#000f14"
        foldable={false}
        hover={false}
        background="rgba(0, 0, 0, 0.7)"
        coloredBars={false}
        footer={footer}
        extra={extra}
      >
        <div className="form-container">
          {error ? (
            <div className={styles.errorContainer}>
              <div className={styles.loginError}>{error}</div>
            </div>
          ) : null}

          {user.logging && !error ? (
            <div className="logging">
              <div className="logging-label">Authenticating...</div>
              <div className="lds-ellipsis">
                {new Array(4).fill(1).map((_, i) => (
                  <div key={`loading-${i}`} />
                ))}
              </div>
            </div>
          ) : (
            <Form onSubmit={onSubmit}>
              {!user.tenants && (
                <Fragment>
                  {user.errored && (
                    <div className="login-error error-message">
                      Access Denied
                    </div>
                  )}
                  <label
                    className={styles.loginLabel}
                    htmlFor="exampleInputEmail1"
                  >
                    Username
                  </label>
                  <Form.Control
                    type="email"
                    id="username"
                    placeholder="Enter your username"
                    autoComplete="username"
                    onChange={(e) => setUsername(e.target.value)}
                    className={styles.loginInput}
                  />
                  <label
                    className={styles.loginLabel}
                    htmlFor="exampleInputPassword1"
                  >
                    Password
                  </label>
                  <Form.Control
                    type="password"
                    id="exampleInputPassword1"
                    placeholder="Password"
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles.loginInput}
                  />
                </Fragment>
              )}

              {user.tenants && (
                <div className="form-group">
                  <div className="login-welcome">
                    Welcome <span className="login-username">{user.name}</span>
                  </div>
                  {user.nickname &&
                    (!user.tenants || user.tenants.length === 0) && (
                      <div className="user-no-tenant">
                        <p>
                          Il tuo profilo non è associato a nessun tenant.
                          <br />
                          Contatta un amministratore AR3S per l&apos;attivazione.
                        </p>
                        <p className={styles.externalIdBlock}>
                          <strong>ID esterno (IdP)</strong> — da comunicare
                          all&apos;amministratore:
                          <br />
                          <code className={styles.externalIdCode}>
                            {user.nickname}
                          </code>
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-light"
                          className="mt-2"
                          onClick={() => {
                            if (user.nickname && navigator.clipboard?.writeText) {
                              navigator.clipboard.writeText(user.nickname);
                            }
                          }}
                        >
                          Copia ID
                        </Button>
                      </div>
                    )}
                  {user.tenants.length > 0 && (
                    <Fragment>
                      <Form.Label>
                        Choose the Tenant you want to operate on...
                      </Form.Label>
                      <Form.Control
                        as="select"
                        id="teantControl"
                        onChange={(e) => setTenant(e.target.value)}
                        className={styles.loginSelect}
                        size="sm"
                      >
                        <option></option>
                        {user.tenants.map((tenant) => (
                          <option key={tenant}>{tenant}</option>
                        ))}
                      </Form.Control>
                      {tenant && (
                        <div style={{ marginTop: "20px" }}>
                          <Form.Label>Pick the Branch...</Form.Label>
                          <Form.Control
                            as="select"
                            id="branchControl"
                            onChange={onChooseBranch}
                            className={styles.loginSelect}
                            size="sm"
                            value={branch ?? ""}
                          >
                            <option></option>
                            {user.branches[tenant]?.map((branch) => (
                              <option key={branch}>{branch}</option>
                            ))}
                          </Form.Control>
                        </div>
                      )}
                    </Fragment>
                  )}
                </div>
              )}
            </Form>
          )}
        </div>
      </Container>
    </div>
  );
}

export default Login;
